import type {
  ApplySwapFreeTargetChangesInput,
  SwapFreeConfigurationTargetDto,
} from '@/api/types/swapFreeControl';

// In-memory staging model for one expanded/edited configuration. Target add/
// remove and per-group exception edits accumulate here and are flushed on Save
// (apply-target-changes). The detail table overlays this before rendering.
export interface StagingModel {
  configId: string;
  isGroups: boolean;
  added: string[];
  removed: string[];
  exceptions: Record<string, number[]>; // group -> override logins
}

export function newStaging(configId: string, isGroups: boolean): StagingModel {
  return { configId, isGroups, added: [], removed: [], exceptions: {} };
}

export function hasChanges(s: StagingModel): boolean {
  return s.added.length > 0 || s.removed.length > 0 || Object.keys(s.exceptions).length > 0;
}

// Add a target: un-remove if it was staged for removal, else append (deduped).
export function stageAdd(s: StagingModel, target: string): StagingModel {
  if (s.removed.includes(target)) {
    return { ...s, removed: s.removed.filter((t) => t !== target) };
  }
  if (s.added.includes(target)) return s;
  return { ...s, added: [...s.added, target] };
}

export function stageAddMany(s: StagingModel, targets: string[]): StagingModel {
  return targets.reduce((acc, t) => stageAdd(acc, t), s);
}

// Remove a target: if it was a staged add, just drop it; else stage removal and
// drop any staged exception override for it.
export function stageRemove(s: StagingModel, target: string): StagingModel {
  if (s.added.includes(target)) {
    const exceptions = { ...s.exceptions };
    delete exceptions[target];
    return { ...s, added: s.added.filter((t) => t !== target), exceptions };
  }
  const exceptions = { ...s.exceptions };
  delete exceptions[target];
  const removed = s.removed.includes(target) ? s.removed : [...s.removed, target];
  return { ...s, removed, exceptions };
}

export function stageExceptions(s: StagingModel, group: string, logins: number[]): StagingModel {
  return { ...s, exceptions: { ...s.exceptions, [group]: logins } };
}

export interface OverlayResult {
  rows: SwapFreeConfigurationTargetDto[];
  totalCount: number;
  stagedKeys: Set<string>;
}

// Overlay staging onto a server page: drop removed rows, apply exception
// overrides, prepend staged adds on page 0, adjust totalCount.
export function applyOverlay(
  s: StagingModel,
  pageRows: SwapFreeConfigurationTargetDto[],
  serverTotal: number,
  isFirstPage: boolean,
): OverlayResult {
  const stagedKeys = new Set<string>();

  const kept = pageRows
    .filter((r) => !s.removed.includes(r.target))
    .map((r) => {
      const ov = s.exceptions[r.target];
      return ov !== undefined ? { ...r, exceptions: ov } : r;
    });

  const addedRows: SwapFreeConfigurationTargetDto[] = isFirstPage
    ? s.added.map((t) => {
        stagedKeys.add(t);
        return { target: t, exceptions: s.exceptions[t] ?? [], remove: null };
      })
    : [];

  // totalCount: server total minus removed (that exist server-side) plus staged adds.
  const removedCount = s.removed.length;
  const total = Math.max(0, serverTotal - removedCount) + s.added.length;

  return { rows: [...addedRows, ...kept], totalCount: total, stagedKeys };
}

export function toApplyInput(s: StagingModel): ApplySwapFreeTargetChangesInput {
  return {
    configurationId: s.configId,
    addedTargets: s.added,
    removedTargets: s.removed,
    groupExceptions: Object.entries(s.exceptions).map(([group, logins]) => ({ group, logins })),
  };
}

export function stagingSummary(s: StagingModel): string {
  const parts: string[] = [];
  if (s.added.length) parts.push(`${s.added.length} added`);
  if (s.removed.length) parts.push(`${s.removed.length} removed`);
  const exc = Object.keys(s.exceptions).length;
  if (exc) parts.push(`${exc} exception change(s)`);
  return parts.length ? parts.join(', ') : 'None';
}
